import { useEffect, useState } from 'react'
import { Plus, Minus } from 'lucide-react'
import { parseMoney } from '@/components/Plan/MoneyInput'
import { formatRevenueFull } from './formatters'

function deriveGrowthPct(base: number, value: number): string {
  if (base <= 0 || value === 0) return ''
  return String(Number((((value - base) / base) * 100).toFixed(2)))
}

// A quarter cell in the By Channel Quarterly matrix: a growth-% input linked
// to a revenue input, plus a fill/deduct button toward the row's target.
export default function PlanInput({
  value,
  base,
  rowGap,
  currency,
  onCommit
}: {
  value: number
  base: number
  rowGap: number
  currency?: string | null
  onCommit: (next: number) => void
}) {
  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState<string>(() => (value === 0 ? '' : formatRevenueFull(value, currency)))
  const [growthFocused, setGrowthFocused] = useState(false)
  const [growthInput, setGrowthInput] = useState<string>(() => deriveGrowthPct(base, value))

  useEffect(() => {
    if (!focused) setDraft(value === 0 ? '' : formatRevenueFull(value, currency))
    if (!growthFocused) setGrowthInput(deriveGrowthPct(base, value))
  }, [value, base, currency, focused, growthFocused])

  const commit = () => {
    setFocused(false)
    const trimmed = draft.trim()
    if (trimmed === '') {
      if (value !== 0) onCommit(0)
      return
    }
    const parsed = parseMoney(trimmed, currency)
    if (parsed == null) return
    const next = Math.max(0, Math.round(parsed))
    if (next !== value) onCommit(next)
  }

  const onGrowthChange = (raw: string) => {
    setGrowthInput(raw)
    if (raw === '') {
      if (value !== 0) onCommit(0)
      return
    }
    if (base <= 0 || raw === '-' || raw.endsWith('.')) return
    const pct = Number(raw)
    if (Number.isNaN(pct)) return
    const next = Math.max(0, Math.round(base * (1 + pct / 100)))
    if (next !== value) onCommit(next)
  }

  const canFill = rowGap > 0
  const canDeduct = rowGap < 0
  const deductAmount = canDeduct ? Math.min(Math.abs(rowGap), value) : 0
  const showButton = canFill || (canDeduct && deductAmount > 0)
  const growthDisabled = base <= 0

  return (
    <div className="flex items-center justify-center gap-0.5">
      <div className="relative shrink-0" style={{ width: 76 }}>
        <input
          type="text"
          className="w-full text-right border rounded-md pl-1 pr-3.5 py-0.5 bg-background tabular-nums outline-none transition-colors hover:border-border focus:border-secondary disabled:bg-muted/40 disabled:text-muted-foreground"
          value={growthInput}
          disabled={growthDisabled}
          onFocus={() => setGrowthFocused(true)}
          onBlur={() => setGrowthFocused(false)}
          onChange={(e) => onGrowthChange(e.target.value)}
          placeholder={growthDisabled ? '—' : ''}
        />
        <span className="pointer-events-none absolute inset-y-0 right-1 flex items-center text-[11px] text-muted-foreground">%</span>
      </div>
      <input
        type="text"
        className="shrink-0 text-right border rounded-md px-1 py-0.5 bg-background tabular-nums outline-none transition-colors hover:border-border focus:border-secondary"
        style={{ width: 132 }}
        value={focused ? draft : (value === 0 ? '' : formatRevenueFull(value, currency))}
        onFocus={(e) => {
          setDraft(value === 0 ? '' : formatRevenueFull(value, currency))
          setFocused(true)
          setTimeout(() => e.target.select(), 0)
        }}
        onBlur={commit}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            ;(e.target as HTMLInputElement).blur()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            setFocused(false)
          }
        }}
        placeholder="0"
      />
      <div className="shrink-0" style={{ width: 16, height: 16 }}>
        {showButton && canFill && (
          <button
            type="button"
            onClick={() => onCommit(Math.max(0, value + rowGap))}
            title={`Fill remaining ${formatRevenueFull(rowGap, currency)}`}
            aria-label="Fill remaining"
            className="inline-flex items-center justify-center rounded transition-colors hover:bg-accent w-full h-full"
            style={{ color: 'var(--muted-foreground)' }}
          >
            <Plus size={14} strokeWidth={2} />
          </button>
        )}
        {showButton && canDeduct && deductAmount > 0 && (
          <button
            type="button"
            onClick={() => onCommit(Math.max(0, value - deductAmount))}
            title={`Deduct excess ${formatRevenueFull(deductAmount, currency)}`}
            aria-label="Deduct excess"
            className="inline-flex items-center justify-center rounded transition-colors hover:bg-accent w-full h-full"
            style={{ color: 'var(--muted-foreground)' }}
          >
            <Minus size={14} strokeWidth={2} />
          </button>
        )}
      </div>
    </div>
  )
}
